import React, { useEffect, useState } from 'react';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { api } from '../helpers/utils';
import { storage } from '../helpers/FirebaseConfig';

const verificationReasons = [
  {
    title: "Know you are talking to a real person",
    copy: "Verification reduces fake profiles and helps people feel safer before they start a chat.",
  },
  {
    title: "Build trust before meeting",
    copy: "A verified profile gives friends and nearby people more confidence when a vibe turns into a real-life plan.",
  },
  {
    title: "Keep Pepchat safer",
    copy: "Manual review adds accountability, so conversations and activities can stay more human and respectful.",
  },
];

const VerificationIcon = () => (
  <VerificationIconWrap aria-hidden="true">
    <svg viewBox="0 0 120 120" fill="none">
      <path
        d="M60 1C66.1 1 70.4 9.1 76.2 10.3C82.3 11.5 89.3 5.6 94.4 8.9C99.5 12.2 97.8 21.3 101.6 25.8C105.6 30.5 114.8 30.5 117.1 36.2C119.4 41.8 112.8 48.3 113.4 54.3C114 60.4 121.4 65.8 119.9 71.7C118.4 77.5 109.4 79.7 106.5 85C103.6 90.3 106.8 99 102.4 103.3C98 107.6 89.4 104.3 84.1 107.2C78.8 110.1 76.6 119.1 70.7 120.5C64.9 121.9 59.5 114.6 53.5 114C47.5 113.4 40.9 120 35.3 117.7C29.6 115.4 29.6 106.1 24.9 102.2C20.4 98.4 11.3 100.1 8 95C4.7 89.9 10.6 82.9 9.4 76.8C8.2 70.9 0 66.6 0 60.5C0 54.4 8.2 50.1 9.4 44.2C10.6 38.1 4.7 31.1 8 26C11.3 20.9 20.4 22.6 24.9 18.8C29.6 14.9 29.6 5.6 35.3 3.3C40.9 1 47.5 7.6 53.5 7C59.5 6.4 54 1 60 1Z"
        fill="#2F80ED"
      />
      <path
        d="M36 61.5L52.5 78L86 43.5"
        stroke="#FFFFFF"
        strokeWidth="13"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </VerificationIconWrap>
);

const VerificationPage = () => {
  const [isPaymentDone, setIsPaymentDone] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [dob, setDob] = useState('');
  const [mobile, setMobile] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [idProof, setIdProof] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [verificationIssue, setVerificationIssue] = useState('');

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const paymentStatus = searchParams.get("paymentDone");
    const userId = searchParams.get("userId");

    setIsPaymentDone(paymentStatus === 'true');

    if (userId) {
     
      fetchUserDetails(userId);
    }
  }, [searchParams]);

  const fetchUserDetails = async (userId) => {
    try {
      const res = await axios.post(
        'https://api1.pepchat.in/get-user',
        {
          userId: userId,
          latLong: { lat: 123, long: 124 }
        },
        {
          headers: {
            Authorization: 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOiI2ODZjMDAxMzI4ZWQwZjRlZjU5YzAxZmQiLCJpYXQiOjE3NTE5MDg3NDEsImV4cCI6MTc4MzAxMjc0MX0.BiCND8H91m_dcUZPWDPvLTerw5DZO5HlPJyz_gPnbdc'
          }
        }
      );
  
      if (res.data.status === 100) {
        const user = res.data.userDetails;
        if (user?.verificationIssue) {
          setVerificationIssue(user.verificationIssue);
        }
      }
    } catch (err) {
      console.error("Error fetching user:", err);
    }
  };
  
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!name || !email || !dob || !mobile || !idProof) {
      alert("Please fill all mandatory fields.");
      return;
    }

    setLoading(true);

    try {
      const storageRef = ref(storage, `verifications/${Date.now()}_${idProof.name}`);
      await uploadBytes(storageRef, idProof);
      const idProofUrl = await getDownloadURL(storageRef);

      const payload = {
        name,
        email,
        dob,
        mobile: "+91" + mobile,
        linkedin,
        idProofUrl
      };

      const res = await api.post('https://api1.pepchat.in/submit-verification', payload);

      if (res.status === 200) {
        setShowSuccessPopup(true);
      } else {
        alert("Submission failed.");
      }
    } catch (err) {
      console.error("Submission error:", err);
      alert("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const SuccessPopup = () => (
    <ModalOverlay>
      <ModalCard>
        <ModalTitle>Verification submitted</ModalTitle>
        <ModalText>Your details have been successfully submitted.</ModalText>
        <ModalText>Our team will review and get back within 12-24 hrs.</ModalText>
        <PrimaryButton type="button" onClick={() => setShowSuccessPopup(false)}>
          Close
        </PrimaryButton>
      </ModalCard>
    </ModalOverlay>
  );

  return (
    <Page>
      <Content>
        <Hero>
          <VerificationIcon />
          <VerifiedLabel>Pepchat verified</VerifiedLabel>
          <HeroTitle>Get verified for safer real-world connection.</HeroTitle>
          <HeroSubtitle>
            Submit once, keep your verified badge, and unlock more trusted chats, vibes, and IRL connections.
          </HeroSubtitle>
        </Hero>

        <WhyGrid>
          {verificationReasons.map((item) => (
            <WhyCard key={item.title}>
              <WhyCardTitle>{item.title}</WhyCardTitle>
              <WhyCardCopy>{item.copy}</WhyCardCopy>
            </WhyCard>
          ))}
        </WhyGrid>

        {verificationIssue && (
          <AlertCard>
            <AlertIcon>⚠️</AlertIcon>
            <div>
              <AlertTitle>Verification Issue</AlertTitle>
              <AlertCopy>{verificationIssue}</AlertCopy>
            </div>
          </AlertCard>
        )}

        {isPaymentDone ? (
          <Card>
            <StatusChip>Payment locked-in</StatusChip>
            <CardTitle>Payment received</CardTitle>
            <CardCopy>Verification is in process.</CardCopy>
            <CardCopy>Pepchat manually checks every submission within 12-24 hrs.</CardCopy>
            <MetaCopy>Did not pass? Your Rs. 99 comes right back.</MetaCopy>

            <Divider />

            <SectionHeading>Mandatory step</SectionHeading>
            <Form onSubmit={handleFormSubmit}>
              <Input placeholder="Full Name" value={name} onChange={e => setName(e.target.value)} required />
              <Input placeholder="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
              <InputGroup>
                <Label>Date of Birth</Label>
                <Input type="date" value={dob} onChange={e => setDob(e.target.value)} required />
              </InputGroup>
              <Input placeholder="Pepchat registered mobile number" value={mobile} onChange={e => setMobile(e.target.value)} required />
              <Input placeholder="LinkedIn Profile URL (optional)" value={linkedin} onChange={e => setLinkedin(e.target.value)} />
              <Label>Upload a government-approved ID</Label>
              <FileInput type="file" onChange={e => setIdProof(e.target.files[0])} accept="image/*,application/pdf" required />
              <HelperText>Used only for verification. Encrypted, private, and deleted after review.</HelperText>
              <PrimaryButton type="submit" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit verification form'}
              </PrimaryButton>
            </Form>

            <Divider />
            <SectionHeading>Need help?</SectionHeading>
            <CardCopy>Contact support if you have questions.</CardCopy>
            <PrimaryButton as="a" href="/#contact">
              Contact support
            </PrimaryButton>
          </Card>
        ) : (
          <>
            <SectionTitle>Why get verified on Pepchat?</SectionTitle>
            <Card>
              <Step>
                <strong>Build trust:</strong> Verification shows you are real before people chat or plan to meet.
              </Step>
              <Step>
                <strong>Connect with confidence:</strong> Verified profiles help reduce fake identity and awkward uncertainty.
              </Step>
            </Card>

            <SectionTitle>Verification process</SectionTitle>
            <Card>
              <Step>1. Make a one-time payment of Rs. 99</Step>
              <Step>2. Fill out a short form with basic details after payment</Step>
              <Step>3. Pepchat manually reviews your details</Step>
              <Step>4. Verified badge goes live within 12-24 hours</Step>
              <Step>5. Not verified? Rs. 99 comes right back</Step>
            </Card>

            <SectionTitle>What you unlock</SectionTitle>
            <Card>
              <Benefit>
                <strong>Lifetime verified badge</strong>
                <span>Pay once, stay verified forever. No renewals.</span>
              </Benefit>
              <Benefit>
                <strong>Visible only to verified users</strong>
                <span>Connect inside safer, moderated spaces.</span>
              </Benefit>
            </Card>
          </>
        )}
      </Content>

      {showSuccessPopup && <SuccessPopup />}
    </Page>
  );
};

const Page = styled.div`
  color: #ffffff;
  min-height: 100vh;
  padding: 5vw 4vw 10vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #000000;
  font-family: "Space Grotesk", "Inter", sans-serif;
  position: relative;
  overflow: hidden;
`;

const Content = styled.div`
  width: 100%;
  max-width: 980px;
  z-index: 2;
`;

const Hero = styled.div`
  max-width: 720px;
  margin: 0 auto 34px;
  text-align: center;
`;

const VerificationIconWrap = styled.div`
  width: 112px;
  height: 112px;
  margin: 0 auto 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;

  svg {
    width: 112px;
    height: 112px;
    display: block;
  }
`;

const VerifiedLabel = styled.div`
  color: #ffffff;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0;
  margin-bottom: 16px;
`;

const HeroTitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 6px;
  color: #ffffff;
`;

const HeroSubtitle = styled.p`
  color: #c9c9c9;
  font-size: 16px;
  line-height: 1.6;
  margin: 0;
`;

const WhyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin: 0 0 28px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const WhyCard = styled.article`
  border: 1px solid #242424;
  background: #050505;
  padding: 20px;
`;

const WhyCardTitle = styled.h3`
  margin: 0 0 10px;
  color: #ffffff;
  font-size: 16px;
`;

const WhyCardCopy = styled.p`
  margin: 0;
  color: #bdbdbd;
  font-size: 14px;
  line-height: 1.6;
`;

const AlertCard = styled.div`
  display: flex;
  gap: 12px;
  border-radius: 16px;
  padding: 14px;
  border: 1px solid rgba(255, 77, 77, 0.4);
  background: rgba(255, 77, 77, 0.08);
  margin-bottom: 24px;
  align-items: center;
`;

const AlertIcon = styled.span`
  font-size: 20px;
`;

const AlertTitle = styled.p`
  margin: 0;
  font-weight: 700;
  color: #ff9d9d;
  font-size: 14px;
`;

const AlertCopy = styled.p`
  margin: 0;
  font-size: 14px;
  color: #fefefe;
`;

const Card = styled.div`
  background: #050505;
  border: 1px solid #242424;
  padding: 22px;
  margin-bottom: 28px;
`;

const StatusChip = styled.div`
  display: inline-flex;
  border: 1px solid #303030;
  padding: 5px 14px;
  font-size: 12px;
  letter-spacing: 0;
  text-transform: uppercase;
  color: #ffffff;
  margin-bottom: 12px;
`;

const CardTitle = styled.h3`
  font-size: 22px;
  margin: 0 0 12px;
`;

const CardCopy = styled.p`
  font-size: 15px;
  color: #d5d6e5;
  margin: 0 0 6px;
`;

const MetaCopy = styled.p`
  font-size: 13px;
  color: #8c91a5;
  margin: 8px 0 0;
`;

const Divider = styled.hr`
  margin: 20px 0;
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const SectionHeading = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 12px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Input = styled.input`
  padding: 14px;
  border: 1px solid #303030;
  background: #000000;
  color: #f3f3f7;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    border-color: #ffffff;
    outline: none;
  }
`;

const FileInput = styled(Input)`
  padding: 11px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 13px;
  color: #c5c7d7;
`;

const HelperText = styled.p`
  font-size: 12px;
  color: #a4a8bc;
  margin: -6px 0 0;
`;

const PrimaryButton = styled.button`
  background: #ffffff;
  color: #000000;
  padding: 14px;
  text-decoration: none;
  text-align: center;
  border: 1px solid #ffffff;
  cursor: pointer;
  font-weight: 700;
  letter-spacing: 0;
  margin-top: 4px;

  &:hover {
    background: #000000;
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 14px;
  letter-spacing: 0;
  text-transform: uppercase;
  color: #ffffff;
`;

const Step = styled.p`
  font-size: 14px;
  color: #f6f6f7;
  margin: 0 0 10px;
  line-height: 1.6;
`;

const Benefit = styled.p`
  font-size: 14px;
  color: #f6f6f7;
  margin: 0 0 12px;
  line-height: 1.6;

  span {
    display: block;
    color: #9da1be;
    font-size: 13px;
    margin-top: 4px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalCard = styled.div`
  background: rgba(8, 8, 16, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 18px;
  padding: 28px;
  text-align: center;
  width: min(420px, 90%);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.55);
`;

const ModalTitle = styled.h3`
  margin: 0 0 12px;
`;

const ModalText = styled.p`
  margin: 0 0 8px;
  color: #d2d3e5;
`;

export default VerificationPage;
